<template>
  <v-container width="100%" id="dd-tool" style="padding-top: 80px;" fluid>
    <v-row>
      <v-col cols="3">
        <h2>Input <code>{{ documentName }}</code></h2><br/>
        <v-textarea
          clearable
          style="font-family: monospace"
          outlined
          cols="150"
          rows="30"
          label="Input data here..."
          @input="updateGraph()"
          v-model="inputData">
        </v-textarea>
      </v-col>
      <v-col cols="2">
        <h2>Operations</h2><br/>
        <h3>Add/remove/join</h3>
          <v-btn block class="mb-1" color="primary" @click="addEdge()"
            :disabled="selected.nodes.length != 2">
            <v-icon>mdi-plus</v-icon> Add edge
          </v-btn>
          <v-btn block class="mb-1" @click="removeEdge" :disabled="selected.edges.length == 0" color="error">
            <v-icon>mdi-minus</v-icon> Remove edge
          </v-btn>
          <v-btn block class="mb-1" @click="selected.label = 'joint'; addEdge()" :disabled="selected.nodes.length != 2" color="success">
            <v-icon>mdi-equal</v-icon> Join nodes
          </v-btn>
        <h3>Label</h3>
          <v-radio-group v-model="selected.label">
            <v-radio v-for="n in labels" :key="n" :label="`${n}`" :value="n" @click="updateLabel"></v-radio>
          </v-radio-group>
      </v-col>
      <v-col cols="5">
        <h2>Annotations (orphans : {{ orphanNodes }})</h2>
        <div class="viewport" style="cursor: grab;" @click="select(null, null)" @mousedown.capture="blockEvents" @wheel.capture="blockEvents">
        <screen ref="screen" height="960">
          <g v-for="edge in graph.edges" @click.stop="select(selected.edges, edge, 1)" :key="edge.id">
            <edge :class="isSelected(selected.edges, edge) && 'selected'"
              :data="edge"
              :nodes="graph.nodes">
            </edge>
            <vlabel :edge="edge" :perc="50"  connector useDrag>
              <small style="cursor: grab; padding: 10px; font-family: monospace;">
                {{ padCenter(edge.label, 15)}}
              </small>
            </vlabel>
          </g>
          <g v-for="node in graph.nodes" :key="node.id">
            <node :data="node" ref="node" :class="isSelected(selected.nodes, node) && 'selected'" :useDrag="node.useDrag">
              <div @click.stop="select(selected.nodes, node, 2)">
                <v-chip x-small color="green lighten-4">{{ node.speaker }} {{ node.turn }}</v-chip>&nbsp;{{ node.label }}
              </div>
            </node>
          </g>
         </screen>
       </div>
      </v-col>
      <v-col cols="2" v-show="inputData">
        <h2>Résultat</h2>
        <v-alert v-show="orphanNodes > 0" border="top" type="warning">
          Check there is no orphan nodes!
        </v-alert>
        <v-btn block v-show="orphanNodes == 0" class="my-1 white--text" color="grey" @click="copyToClipboard(finalResult)">
          <v-icon>mdi-clipboard</v-icon> {{ copyState }}
        </v-btn>
        <v-btn class="mb-1 white--text" block color="red" @click="download">
          <v-icon>mdi-download</v-icon> Download
        </v-btn>

        <v-textarea ref="result"
          style="font-family: monospace"
          outlined
          cols="150"
          rows="30"
          readonly
          v-model="finalResult">
        </v-textarea>
      </v-col>
    </v-row>
  </v-container>
</template>


<script>
import Graph from 'vnodes/src/graph';
import Screen from 'vnodes/src/components/Screen.vue';
import Edge from 'vnodes/src/components/Edge.vue';
import Node from 'vnodes/src/components/Node.vue';
import Label from 'vnodes/src/components/Label.vue';
import uuidv4 from 'uuid';

export default {
  name: 'Tool',
  components: {screen: Screen, Node, Edge, vlabel: Label},
  computed: {
    // Return number of orphan nodes
    orphanNodes() {
      return this.graph.nodes.filter((x) => (x.orphan)).length;
    },

    // Build final result JSON-string
    finalResult() {
      const result = Object();

      // Document ID
      result.id = this.documentName;

      // List of EDU
      result.edus = this.graph.nodes.map((x) => ({
        text: x.label,
        speaker: x.speaker,
        eduidx: x.id - 1, // bug in graph.js from/to cannot be equal to 0
        turnidx: x.turn,
      }));

      // List of relations
      result.relations = this.graph.edges.map((x) => ({
        x: x.from.id,
        y: x.to.id,
        type: x.label,
      }));

      return JSON.stringify(result, null, 2);
    },
  },
  methods: {
    padCenter: (str, max) => (str.padStart(str.length + Math.floor((max - str.length) / 2), ' ').padEnd(max, ' ')),

    copyToClipboard(str) {
      const el = document.createElement('textarea');
      el.value = str;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      this.copyState = 'Copied!';
    },

    // Download final JSON file
    download() {
      // Build URL to file
      const url = URL.createObjectURL(new Blob([this.finalResult]), { type: 'application/txt' });

      // Add and configure a link element
      const link = document.createElement('a');
      link.href = url;
      link.download = `annotations-${this.documentName}.json`;
      document.body.appendChild(link);

      // Simulate a click to download
      link.click();

      // Remove element
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },

    // Build nodes from input data
    updateGraph() {

      // Split input into lines
      const parts = this.inputData.replace('\n\n', '').trim().split('\n');

      if (parts.length < 2)
        return;

      // Extract document ID
      this.documentName = parts[0];

      // Nodes content starts at index 1 of previous array
      const textNodes = parts.slice(1)

      // Build a node for each line
      const nodes = textNodes.map((text, id) => ({
        id: id+1, // bug in graph.js
        speaker: text[0],
        turn: parseInt(text.split(':')[0].split('-')[1], 10),
        label: text.split(':')[1].trim(),
        x: 10,
        y: 100*id,
        useDrag: true,
        orphan: true,
      }));

      // Reset graph
      this.graph.reset();

      // Add each node to graph structure
      for (const node of nodes) {
        this.graph.createNode({...node});
      }

      // Center and zoom
      this.$nextTick(() => {
        this.$refs.screen.zoomNodes(this.graph.nodes, {scale: 1})
      })

      if (this.$refs.node)
        this.$nextTick(() => {
          this.$refs.node.forEach(node => {
            node.fitContent()
          })
        });
    },


    // Add an edge between 2 nodes
    addEdge() {

      // Order nodes bu EDU ID
      const nodes = this.selected.nodes.sort((a,b) => (a.id > b.id))

      // Don't to anything if there is already an edge
      if (this.graph.edges.find((x) => (x.from == nodes[0].id && x.to == nodes[1].id)))
        return

      // Otherwise, create an edge
      this.graph.createEdge({
        id: uuidv4(),
        from: nodes[0],
        to: nodes[1],
        type: 'smooth',
        label: this.selected.label,
        toAnchor: {x:'100%', y:'50%'},
        fromAnchor: {x:'100%', y:'50%'},
      })

      // Set linked nodes to not orphan
      this.graph.updateNode(nodes[0], {orphan: false});
      this.graph.updateNode(nodes[1], {orphan: false});

      // Reset copy state
      this.copyState = 'Copy to clipboard';

    },

    // True if the node with ID nodeId is isolated
    isOrphan(nodeId) {
      return this.graph.edges.filter((x) => (x.from == nodeId || x.to == nodeId)).length == 0;
    },

    // Remove edge between 2 nodes
    removeEdge() {

      // Get selected edge ID
      const selected = this.selected.edges[0];

      // Remove edge from graph structure
      this.graph.edges = this.graph.edges.filter((x) => (x.id != selected.id));

      // Update orphan state for the nodes of deleted edge
      const from = selected.from;
      const to =  selected.to;
      this.graph.nodes.find((x) => (x.id == to.id)).orphan = this.isOrphan(to.id);
      this.graph.nodes.find((x) => (x.id == from.id)).orphan = this.isOrphan(from.id);

      // Clean selected edges list
      this.selected.edges = [];

      // Reset copy state
      this.copyState = 'Copy to clipboard';
    },

    // Change the selected edge label
    updateLabel() {

      // If an edge is selected
      if (this.selected.edges.length > 0) {
        // Update its label
        this.selected.edges[0].label = this.selected.label;
      }

      // Reset copy state
      this.copyState = 'Copy to clipboard';
    },

    // Put an object into the box
    select (box, obj, capacity=2) {

      // Clean all boxes if parameter is null
      if (!box) {
        this.selected.edges = []
        this.selected.nodes = []
        return;
      }

      // If box capacity is excedeed, remove first element (node selection)
      if(box && box.length > capacity-1)
        box.shift();

      // If object is not already selected, put it in the box (=select it)
      if(!this.isSelected(box, obj))
        box.push(obj);
    },

    // True if the object is in the box
    isSelected (box, obj) {
      return box && box.find((x) => (x.id == obj.id));
    },

    // Graph stuff, taken from example
    blockEvents (e) {
      const path = e.path || e.composedPath?.();
      if (path?.find(el => el.classList?.contains('no-drag'))) { // @mousedown
        const pz = this.$refs.screen.panzoom
        pz.options.preventMouseEventsDefault = false // enable default events (text select, input click)
        document.addEventListener('mouseup', () => {
          pz.options.preventMouseEventsDefault = true
        }, { once: true })
        e.stopPropagation() // disable node drag
      }
      if (path?.find(el => el.classList?.contains('no-wheel'))) { // @wheel
        e.stopPropagation() // disable wheel zoom
      }
    },
  },

  data: () => ({
    inputData: '',
    //inputData: "stac-1\nA-0: Anybody have any sheep or wheat ?\nB-1: No .\nC-2: Sorry need my sheep .\nA-3: I have clay .\nC-4: Still no .",
    graph: new Graph(),
    documentName: '',
    labels: [
      'QA_pair', 'Comment', 'Acknowledgment', 'Continuation', 'Elaboration',
      'Q-Elab', 'Result', 'Contrast', 'Explanation', 'Clarification_Q',
      'Parallel', 'Correction', 'Alternation', 'Narration', 'Conditional', 'Background'
    ],
    copyState: 'Copy to clipboard',
    selected: {
      nodes: [],
      edges: [],
      label: 'QA_pair',
    },
  }),
}
</script>

<style>
#dd-tool .node .background {
  background-color: #ccc;
}
#dd-tool .node .content > div {
  padding: 3px;
}
#dd-tool .node .content h4,h5,p {
  margin: 0
}
#dd-tool .node:hover .background {
  background-color: #DDDD00;
}
#dd-tool .node.selected .content {
  background-color: #E1E100;
  box-shadow: 0px 0px 0px 3px #AAAA00;
}
#dd-tool .node .content {
  cursor: pointer;
}
#dd-tool .edge {
  stroke-width: 3;
  cursor: pointer;
}
#dd-tool .edge:hover {
  stroke-width: 6;
  stroke: rgb(90 200 90);
}
#dd-tool .edge.selected {
  stroke-width: 6;
  stroke: #FF3333;
}
</style>
